import type {
  Question,
  Stats,
  QuestionType,
  AppMode,
  GamificationData,
} from "@/types";
import { QuestionDisplay } from "@/ui/question-display";
import { eventManager } from "@/utils/event-manager";
import { dialogManager } from "@/ui/dialog-manager";

/**
 * Interface for DOM elements used by the UI Manager
 */
interface UIElements {
  // Question elements
  questionText: HTMLElement | null;
  answerInput: HTMLInputElement | null;
  multipleChoiceOptions: HTMLElement | null;

  // Control buttons
  checkBtn: HTMLButtonElement | null;
  newQuestionBtn: HTMLButtonElement | null;
  showAnswerBtn: HTMLButtonElement | null;

  // Feedback
  feedback: HTMLElement | null;

  // Stats and gamification
  correctCount: HTMLElement | null;
  totalCount: HTMLElement | null;
  accuracyPercent: HTMLElement | null;
  levelNumber: HTMLElement | null;
  xpCurrent: HTMLElement | null;
  xpNext: HTMLElement | null;
  xpBar: HTMLElement | null;
  streakCurrent: HTMLElement | null;
  streakBest: HTMLElement | null;

  // Mode controls
  modeSelect: HTMLSelectElement | null;
  questionTypeSelect: HTMLSelectElement | null;
  smartModeToggle: HTMLInputElement | null;
  autoAdvanceToggle: HTMLInputElement | null;

  // UI containers
  trainerTitle: HTMLElement | null;
  messageContainer: HTMLElement | null;
  achievementPopup: HTMLElement | null;
  achievementNotification: HTMLElement | null;

  // Stats dashboard
  detailedStats: HTMLElement | null;
  statsContent: HTMLElement | null;
}

declare global {
  interface Window {
    FeedbackSystem: any;
    StatsDashboard: any;
  }
}

/**
 * Type-safe UI Manager that coordinates all UI components and interactions
 * Handles DOM manipulation, event management, and component coordination
 */
export class UIManager {
  private readonly elements: UIElements;
  private questionDisplay!: QuestionDisplay;
  private feedbackSystem: any; // TODO: Type when FeedbackSystem is converted
  private statsDashboard: any; // TODO: Type when StatsDashboard is converted

  constructor() {
    this.elements = this.initializeElements();
    this.initializeComponents();
    this.setupEventListeners();
    this.setupKeyboardShortcuts();
    this.initializeAutoAdvance();
  }

  /**
   * Initialize all UI components
   */
  private initializeComponents(): void {
    // Create safe elements for QuestionDisplay
    const questionDisplayElements = {
      questionText: this.elements.questionText!,
      answerInput: this.elements.answerInput!,
      multipleChoiceOptions: this.elements.multipleChoiceOptions!,
      checkBtn: this.elements.checkBtn!,
      showAnswerBtn: this.elements.showAnswerBtn!,
      feedback: this.elements.feedback!,
    };

    this.questionDisplay = new QuestionDisplay(questionDisplayElements);
    this.feedbackSystem = new window.FeedbackSystem(this.elements);
    this.initializeDropdownMenu();
    this.initializeHeaderActions();
    // Stats dashboard will be initialized by the main app with dependencies
  }

  /**
   * Initialize the stats dashboard with the required dependencies
   */
  public initializeStatsDashboard(
    statsTracker: any,
    gamificationSystem: any,
    achievementSystem: any
  ): void {
    this.statsDashboard = new window.StatsDashboard(
      this.elements,
      statsTracker,
      gamificationSystem,
      achievementSystem
    );
  }

  /**
   * Initialize DOM elements with type safety
   */
  private initializeElements(): UIElements {
    const elements = {
      // Question elements
      questionText: document.getElementById("question-text"),
      answerInput: document.getElementById("answer-input") as HTMLInputElement,
      multipleChoiceOptions: document.getElementById("multiple-choice-options"),

      // Control buttons
      checkBtn: document.getElementById("check-btn") as HTMLButtonElement,
      newQuestionBtn: document.getElementById(
        "new-question-btn"
      ) as HTMLButtonElement,
      showAnswerBtn: document.getElementById(
        "show-answer-btn"
      ) as HTMLButtonElement,

      // Feedback
      feedback: document.getElementById("feedback"),

      // Stats and gamification
      correctCount: document.getElementById("correct-count"),
      totalCount: document.getElementById("total-count"),
      accuracyPercent: document.getElementById("accuracy"), // Note: HTML uses "accuracy", not "accuracy-percent"
      levelNumber: document.getElementById("level-number"),
      xpCurrent: document.getElementById("xp-text"), // This contains the XP text like "0 / 100 XP"
      xpNext: document.getElementById("xp-text"), // Same element, we'll parse it
      xpBar: document.getElementById("xp-fill"), // This is the progress bar fill
      streakCurrent: document.getElementById("current-streak"),
      streakBest: document.getElementById("daily-streak"), // Using daily-streak as "best" for now

      // Mode controls
      modeSelect: document.getElementById("mode-select") as HTMLSelectElement,
      questionTypeSelect: document.getElementById(
        "question-type-select"
      ) as HTMLSelectElement,
      smartModeToggle: document.getElementById(
        "smart-mode-toggle"
      ) as HTMLInputElement,
      autoAdvanceToggle: document.getElementById(
        "auto-advance-toggle"
      ) as HTMLInputElement,

      // UI containers
      trainerTitle: document.getElementById("trainer-title"),
      messageContainer: document.getElementById("message-container"),
      achievementPopup: document.getElementById("achievement-popup"),
      achievementNotification: document.getElementById(
        "achievement-notification"
      ),

      // Stats dashboard
      detailedStats: document.getElementById("detailed-stats"),
      statsContent: document.getElementById("stats-content"),
    };

    return elements;
  }

  /**
   * Setup event listeners for UI interactions
   */
  private setupEventListeners(): void {
    // Check answer button
    this.elements.checkBtn?.addEventListener("click", () => {
      eventManager.emit("ui:check-answer", undefined);
    });

    // New question button
    this.elements.newQuestionBtn?.addEventListener("click", () => {
      eventManager.emit("ui:new-question", undefined);
    });

    // Show answer button
    this.elements.showAnswerBtn?.addEventListener("click", () => {
      eventManager.emit("ui:show-answer", undefined);
    });

    // Answer input - Enter key to check answer
    this.elements.answerInput?.addEventListener("keypress", (e) => {
      if (e.key === "Enter" && !this.elements.answerInput?.disabled) {
        eventManager.emit("ui:check-answer", undefined);
        e.stopPropagation();
      }
    });

    // Global Enter key when feedback is showing - triggers new question
    document.addEventListener("keypress", (e) => {
      if (
        e.key === "Enter" &&
        (this.elements.answerInput?.disabled ||
          this.elements.feedback?.style.display !== "none")
      ) {
        eventManager.emit("ui:new-question", undefined);
      }
    });

    // Mode selection
    this.elements.modeSelect?.addEventListener("change", (e) => {
      const target = e.target as HTMLSelectElement;
      eventManager.emit("mode-changed", { type: target.value } as AppMode);
    });

    // Question type selection
    this.elements.questionTypeSelect?.addEventListener("change", (e) => {
      const target = e.target as HTMLSelectElement;
      eventManager.emit("question-type-changed", {
        type: target.value,
      } as QuestionType);
    });

    // Smart mode toggle
    this.elements.smartModeToggle?.addEventListener("change", (e) => {
      const target = e.target as HTMLInputElement;
      this.toggleSmartMode(target.checked);
    });

    // Auto-advance toggle
    this.elements.autoAdvanceToggle?.addEventListener("change", (e) => {
      const target = e.target as HTMLInputElement;
      this.toggleAutoAdvance(target.checked);
    });

    // Mode buttons (for manual mode)
    document.querySelectorAll(".mode-btn").forEach((btn) => {
      btn.addEventListener("click", () => {
        if (this.isSmartModeEnabled()) return; // Ignore if smart mode is enabled

        const button = btn as HTMLButtonElement;
        const mode = button.dataset.mode;
        if (mode) {
          this.setActiveMode(mode);
          eventManager.emit("mode-changed", { type: mode } as AppMode);
        }
      });
    });

    // Question type cards (for manual mode)
    document.querySelectorAll(".question-type-card").forEach((card) => {
      card.addEventListener("click", () => {
        const cardElement = card as HTMLElement;
        const type = cardElement.dataset.type;
        if (type) {
          this.setActiveQuestionType(type);
          eventManager.emit("question-type-changed", { type } as QuestionType);
        }
      });
    });

    // Reset stats dialog
    const resetStatsBtn = document.getElementById("reset-stats-btn");
    const resetDialog = document.getElementById("reset-dialog");
    const confirmResetBtn = document.getElementById("confirm-reset-btn");
    const cancelResetBtn = document.getElementById("cancel-reset-btn");

    resetStatsBtn?.addEventListener("click", () => {
      if (resetDialog) resetDialog.style.display = "block";
    });

    cancelResetBtn?.addEventListener("click", () => {
      if (resetDialog) resetDialog.style.display = "none";
    });

    confirmResetBtn?.addEventListener("click", async () => {
      await this.confirmReset();
      // Dialog will be closed by the reset completion handler
    });

    // Listen for reset completion to close dialog
    eventManager.on("reset-completed", () => {
      if (resetDialog) resetDialog.style.display = "none";
    });

    // View detailed stats
    const viewStatsBtn = document.getElementById("view-stats-btn");
    const closeStatsBtn = document.getElementById("close-stats-btn");
    const detailedStats = document.getElementById("detailed-stats");

    viewStatsBtn?.addEventListener("click", () => {
      // Use smart scroll with visual feedback
      this.showStatsWithAttention();
    });

    closeStatsBtn?.addEventListener("click", () => {
      if (detailedStats) detailedStats.style.display = "none";
    });

    // Help modal
    const helpBtn = document.getElementById("help-btn");
    const helpModal = document.getElementById("help-modal");
    const closeHelpBtn = document.getElementById("close-help-btn");

    helpBtn?.addEventListener("click", () => {
      if (helpModal) helpModal.style.display = "block";
    });

    closeHelpBtn?.addEventListener("click", () => {
      if (helpModal) helpModal.style.display = "none";
    });

    // Notifications button
    const notificationsBtn = document.getElementById("notifications-btn");
    notificationsBtn?.addEventListener("click", async () => {
      // Call global notification initialization function
      if ((window as any).initializeNotifications) {
        const success = await (window as any).initializeNotifications();
        const status = (window as any).getNotificationStatus?.() || {
          success,
          message: success
            ? "Notifications enabled!"
            : "Failed to enable notifications.",
          isDevelopment: false,
        };

        if (status.success) {
          await dialogManager.showSuccess(`🔔 ${status.message}`, {
            title: "Notifications Enabled",
          });
        } else {
          await dialogManager.showError(
            status.isDevelopment
              ? `ℹ️ ${status.message}`
              : `❌ ${status.message}`,
            {
              title: status.isDevelopment
                ? "Development Mode"
                : "Notification Error",
            }
          );
        }
      } else {
        await dialogManager.showError("❌ Notification system not available.", {
          title: "Notification Error",
        });
      }
    });

    // Close modals with Escape key and other keyboard shortcuts
    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape") {
        if (resetDialog) resetDialog.style.display = "none";
        if (detailedStats) detailedStats.style.display = "none";
        if (helpModal) helpModal.style.display = "none";
      }
    });

    // Progressive info button
    const progressiveInfoBtn = document.getElementById("progressive-info-btn");
    progressiveInfoBtn?.addEventListener("click", () => {
      this.showProgressiveInfoTooltip();
    });

    // Setup application event listeners for gamification updates
    this.setupApplicationEventListeners();
  }

  /**
   * Setup application event listeners for gamification and achievements
   */
  private setupApplicationEventListeners(): void {
    // Listen for gamification updates (XP, level, streaks)
    eventManager.on("gamification-updated", (data) => {
      this.updateGamificationDisplay(data);
    });

    // Listen for achievement unlocks
    eventManager.on("achievement-unlocked", (achievements) => {
      this.showAchievementNotification(achievements);
    });

    // Listen for stats updates to update basic counters
    eventManager.on("stats-updated", (stats) => {
      this.updateBasicStats(stats);
    });
  }

  /**
   * Setup comprehensive keyboard shortcuts
   */
  private setupKeyboardShortcuts(): void {
    document.addEventListener("keydown", (e) => {
      // Only handle shortcuts when not typing in an input field (except for Enter)
      if (document.activeElement?.tagName === "INPUT" && e.key !== "Enter") {
        return;
      }

      // Only handle shortcuts when focused on body or other non-input elements
      if (document.activeElement?.tagName === "BODY" || e.key === "Enter") {
        switch (e.key) {
          case " ": // Space - Generate new question
            e.preventDefault();
            if (!this.elements.answerInput?.disabled) {
              eventManager.emit("ui:new-question", undefined);
            }
            break;

          case "Enter": // Enter - Check answer or next question (context-aware)
            e.preventDefault();
            if (
              this.elements.answerInput?.disabled &&
              this.elements.feedback?.style.display !== "none"
            ) {
              // If answer input is disabled and feedback is showing, get new question
              eventManager.emit("ui:new-question", undefined);
            } else if (!this.elements.checkBtn?.disabled) {
              // Otherwise, check the current answer
              eventManager.emit("ui:check-answer", undefined);
            }
            break;

          case "1": // Mode selection shortcuts (only in manual mode)
          case "2":
          case "3":
          case "4":
            if (!this.isSmartModeEnabled()) {
              e.preventDefault();
              const modes = ["all", "present", "past", "subjunctive"];
              const mode = modes[parseInt(e.key) - 1];
              if (mode) {
                this.setActiveMode(mode);
                eventManager.emit("mode-changed", { type: mode } as AppMode);
              }
            }
            break;

          case "s": // Show detailed statistics
          case "S":
            e.preventDefault();
            this.showDetailedStats();
            break;

          case "Escape": // Close modals
            this.hideDetailedStats();
            if (this.elements.achievementNotification) {
              this.elements.achievementNotification.style.display = "none";
            }
            const helpModal = document.getElementById("help-modal");
            const resetDialog = document.getElementById("reset-dialog");
            if (helpModal) helpModal.style.display = "none";
            if (resetDialog) resetDialog.style.display = "none";
            break;

          case "?": // Show help modal
            e.preventDefault();
            const modal = document.getElementById("help-modal");
            if (modal) modal.style.display = "block";
            break;

          case "h": // Show help modal (alternative)
          case "H":
            e.preventDefault();
            const helpModalAlt = document.getElementById("help-modal");
            if (helpModalAlt) helpModalAlt.style.display = "block";
            break;

          case "r": // Reset stats (with confirmation)
          case "R":
            e.preventDefault();
            const resetDialogAlt = document.getElementById("reset-dialog");
            if (resetDialogAlt) resetDialogAlt.style.display = "block";
            break;
        }
      }
    });
  }

  /**
   * Display a question in the UI
   */
  public displayQuestion(question: Question): void {
    if (!question) {
      console.warn("No question provided to display");
      return;
    }

    try {
      this.questionDisplay.display(question);
      this.clearFeedback();
      this.focusAnswerInput();

      // Reset the New Question button styling when displaying a new question
      this.resetNewQuestionButton();

      // Auto-scroll to keep question in focus when content changes
      setTimeout(() => {
        this.scrollToQuestion();
      }, 100);
    } catch (error) {
      console.error("Error displaying question:", error);
      this.showMessage("Error displaying question", "error");
    }
  }

  /**
   * Get the user's answer from the input
   */
  public getUserAnswer(): string {
    if (!this.elements.answerInput) {
      console.warn("Answer input element not found");
      return "";
    }

    return this.elements.answerInput.value.trim();
  }

  /**
   * Get selected multiple choice option
   */
  public getSelectedOption(): string | null {
    const selected = this.elements.multipleChoiceOptions?.querySelector(
      ".choice-option.selected"
    );
    return selected ? (selected as HTMLElement).dataset.answer || null : null;
  }

  /**
   * Show feedback for the user's answer
   */
  public showFeedback(
    isCorrect: boolean,
    message: string,
    question?: any
  ): void {
    try {
      this.feedbackSystem?.show(isCorrect, message, question);

      // Auto-scroll to keep feedback in view after examples appear
      setTimeout(() => {
        this.scrollToFeedback();
      }, 100);

      // For multiple choice questions, make the New Question button prominent
      if (question?.type === "multiple-choice") {
        this.highlightNewQuestionButton();

        // If auto-advance is enabled, automatically proceed after a delay
        if (this.isAutoAdvanceEnabled()) {
          setTimeout(() => {
            eventManager.emit("ui:new-question", undefined);
          }, 2000); // 2 second delay to show feedback
        }
      }
    } catch (error) {
      // Error handling
    }
  }

  /**
   * Highlight the New Question button to make it prominent after answering
   */
  private highlightNewQuestionButton(): void {
    const newQuestionBtn = this.elements.newQuestionBtn;
    if (!newQuestionBtn) return;

    // Check if auto-advance is enabled
    const isAutoAdvanceEnabled = this.isAutoAdvanceEnabled();

    if (isAutoAdvanceEnabled) {
      // Auto-advance mode: show countdown
      newQuestionBtn.classList.add("btn-prominent", "auto-advance");
      newQuestionBtn.textContent = "⚡ Auto-advancing in 2...";

      // Countdown animation
      let countdown = 2;
      const countdownInterval = setInterval(() => {
        countdown--;
        if (countdown > 0) {
          newQuestionBtn.textContent = `⚡ Auto-advancing in ${countdown}...`;
        } else {
          newQuestionBtn.textContent = "⚡ Loading next question...";
          clearInterval(countdownInterval);
        }
      }, 1000);
    } else {
      // Manual mode: highlight button for user action
      newQuestionBtn.classList.add("btn-prominent", "pulse-animation");
      newQuestionBtn.textContent = "→ Continue to Next Question";
    }

    // Add common styling
    newQuestionBtn.style.boxShadow = "0 0 20px rgba(102, 126, 234, 0.5)";
    newQuestionBtn.style.background = "#667eea";
    newQuestionBtn.style.color = "white";
    newQuestionBtn.style.fontWeight = "bold";
    newQuestionBtn.style.transform = "scale(1.05)";
    newQuestionBtn.style.transition = "all 0.3s ease";
  }

  /**
   * Reset the New Question button to normal styling
   */
  private resetNewQuestionButton(): void {
    const newQuestionBtn = this.elements.newQuestionBtn;
    if (!newQuestionBtn) return;

    // Remove all prominent styling classes
    newQuestionBtn.classList.remove(
      "btn-prominent",
      "pulse-animation",
      "auto-advance"
    );
    newQuestionBtn.textContent = "New Question";

    // Reset to default secondary button styles
    newQuestionBtn.style.boxShadow = "";
    newQuestionBtn.style.background = "";
    newQuestionBtn.style.color = "";
    newQuestionBtn.style.fontWeight = "";
    newQuestionBtn.style.transform = "";
    newQuestionBtn.style.transition = "";
  }

  /**
   * Show the correct answer without feedback
   */
  public showAnswer(answer: string, question?: Question): void {
    try {
      const message = `The correct answer is: ${answer}`;
      this.feedbackSystem?.show(false, message, question);
    } catch (error) {
      console.error("Error showing answer:", error);
    }
  }

  /**
   * Clear any existing feedback
   */
  public clearFeedback(): void {
    try {
      this.feedbackSystem?.clear();
    } catch (error) {
      console.error("Error clearing feedback:", error);
    }
  }

  /**
   * Update statistics display
   */
  public updateStatsDisplay(stats: Stats): void {
    try {
      if (this.elements.correctCount) {
        this.elements.correctCount.textContent = stats.correct.toString();
      }
      if (this.elements.totalCount) {
        this.elements.totalCount.textContent = stats.total.toString();
      }
      if (this.elements.accuracyPercent) {
        this.elements.accuracyPercent.textContent = `${stats.accuracy}%`;
      }

      // Update stats dashboard if available
      this.statsDashboard?.updateStatsDisplay?.(stats);

      // If detailed stats are currently visible, refresh their content
      this.refreshDetailedStatsIfVisible();
    } catch (error) {
      console.error("Error updating stats display:", error);
    }
  }

  /**
   * Refresh detailed stats content if the detailed stats panel is currently visible
   */
  private refreshDetailedStatsIfVisible(): void {
    try {
      const detailedStats = document.getElementById("detailed-stats");
      if (detailedStats && detailedStats.style.display === "block") {
        // Detailed stats are currently visible, refresh their content
        if (this.statsDashboard && this.statsDashboard.show) {
          this.statsDashboard.show();
        }
        // Also emit the event as backup
        eventManager.emit("ui:show-detailed-stats", undefined);
      }
    } catch (error) {
      console.error("Error refreshing detailed stats:", error);
    }
  }

  /**
   * Update gamification display
   */
  public updateGamificationDisplay(data: GamificationData): void {
    try {
      // Update level
      if (this.elements.levelNumber) {
        this.elements.levelNumber.textContent = data.level?.toString() || "1";
      }

      // Update XP text (format: "current / next XP")
      if (this.elements.xpCurrent) {
        const currentXP = data.xp.total || 0;
        const totalForNextLevel = currentXP + (data.xp.pointsToNextLevel || 0);
        const xpText = `${currentXP} / ${totalForNextLevel} XP`;
        this.elements.xpCurrent.textContent = xpText;
      }

      // Update XP bar
      this.updateXPBar(data.xpProgress || 0);

      // Update current streak
      if (this.elements.streakCurrent) {
        this.elements.streakCurrent.textContent =
          data.streaks.current?.toString() || "0";
      }

      // Update daily streak
      if (this.elements.streakBest) {
        this.elements.streakBest.textContent =
          data.streaks.daily?.toString() || "0";
      }

      // Update stats dashboard if available
      this.statsDashboard?.updateGamificationDisplay?.(data);
    } catch (error) {
      console.error("Error updating gamification display:", error);
    }
  }

  /**
   * Update XP progress bar
   */
  private updateXPBar(progressPercentage: number): void {
    if (!this.elements.xpBar) {
      return;
    }

    const percentage = Math.min(Math.max(progressPercentage, 0), 100);
    this.elements.xpBar.style.width = `${percentage}%`;
  }

  /**
   * Set the trainer title
   */
  public setTitle(verbName: string): void {
    if (this.elements.trainerTitle) {
      this.elements.trainerTitle.textContent = verbName;
    }
  }

  /**
   * Show a message to the user
   */
  public showMessage(
    message: string,
    type: "success" | "error" | "info" = "info"
  ): void {
    if (!this.elements.messageContainer) {
      console.log(`[${type.toUpperCase()}] ${message}`);
      return;
    }

    this.elements.messageContainer.textContent = message;
    this.elements.messageContainer.className = `message ${type}`;
    this.elements.messageContainer.style.display = "block";

    // Auto-hide after 3 seconds
    setTimeout(() => {
      if (this.elements.messageContainer) {
        this.elements.messageContainer.style.display = "none";
      }
    }, 3000);
  }

  /**
   * Show achievements popup
   */
  public showAchievements(achievements: string[]): void {
    if (!achievements.length) return;

    try {
      this.statsDashboard?.showAchievements?.(achievements);
    } catch (error) {
      console.error("Error showing achievements:", error);
      // Fallback: show as message
      this.showMessage(
        `Achievement unlocked: ${achievements.join(", ")}`,
        "success"
      );
    }
  }

  /**
   * Show daily bonus notification
   */
  public showDailyBonus(): void {
    this.showMessage("Daily bonus awarded! +10 XP", "success");
  }

  /**
   * Focus on the answer input field
   */
  public focusAnswerInput(): void {
    try {
      this.elements.answerInput?.focus();
    } catch (error) {
      console.error("Error focusing answer input:", error);
    }
  }

  /**
   * Clear the answer input field
   */
  public clearAnswerInput(): void {
    if (this.elements.answerInput) {
      this.elements.answerInput.value = "";
    }
  }

  /**
   * Initialize smart mode settings
   */
  public initializeSmartMode(): void {
    try {
      // Set initial state based on stored preference (default to true for smart mode)
      const smartModeEnabled =
        localStorage.getItem("smart-mode-enabled") !== "false"; // Default to true
      if (this.elements.smartModeToggle) {
        this.elements.smartModeToggle.checked = smartModeEnabled;
      }

      // Apply the initial UI state
      this.toggleSmartMode(smartModeEnabled);
    } catch (error) {
      console.error("Error initializing smart mode:", error);
    }
  }

  /**
   * Toggle smart mode on/off
   */
  private toggleSmartMode(enabled: boolean): void {
    try {
      localStorage.setItem("smart-mode-enabled", enabled.toString());

      // Update UI based on smart mode state
      const progressiveDisplay = document.getElementById(
        "progressive-challenge-display"
      );
      const manualSelector = document.getElementById(
        "manual-question-selector"
      );
      const manualModeSection = document.querySelector(
        ".manual-mode-section"
      ) as HTMLElement;

      if (enabled) {
        // Smart mode enabled
        eventManager.emit("mode-changed", { type: "smart" });
        if (progressiveDisplay) progressiveDisplay.style.display = "block";
        if (manualSelector) manualSelector.style.display = "none";
        if (manualModeSection) manualModeSection.style.display = "none";
      } else {
        // Smart mode disabled - switch to manual mode (default to present)
        eventManager.emit("mode-changed", { type: "present" });
        this.setActiveMode("present");
        if (progressiveDisplay) progressiveDisplay.style.display = "none";
        if (manualSelector) manualSelector.style.display = "block";
        if (manualModeSection) manualModeSection.style.display = "block";
      }

      eventManager.emit("smart-mode-toggled", enabled);
    } catch (error) {
      console.error("Error toggling smart mode:", error);
    }
  }

  /**
   * Toggle auto-advance mode on/off
   */
  private toggleAutoAdvance(enabled: boolean): void {
    localStorage.setItem("auto-advance-enabled", enabled.toString());
  }

  /**
   * Check if auto-advance is enabled
   */
  public isAutoAdvanceEnabled(): boolean {
    return localStorage.getItem("auto-advance-enabled") === "true";
  }

  /**
   * Initialize auto-advance settings
   */
  public initializeAutoAdvance(): void {
    const autoAdvanceEnabled =
      localStorage.getItem("auto-advance-enabled") === "true";
    if (this.elements.autoAdvanceToggle) {
      this.elements.autoAdvanceToggle.checked = autoAdvanceEnabled;
    }
  }

  /**
   * Show detailed statistics modal
   */
  public showDetailedStats(): void {
    const detailedStats = document.getElementById("detailed-stats");
    if (detailedStats) {
      detailedStats.style.display = "block";
    }
  }

  /**
   * Hide detailed statistics modal
   */
  public hideDetailedStats(): void {
    const detailedStats = document.getElementById("detailed-stats");
    if (detailedStats) {
      detailedStats.style.display = "none";
    }
  }

  /**
   * Set the active mode for manual mode selection
   */
  private setActiveMode(mode: string): void {
    // Remove active class from all mode buttons
    const modeButtons = document.querySelectorAll(".mode-btn");
    modeButtons.forEach((btn) => btn.classList.remove("active"));

    // Add active class to selected mode button
    const selectedBtn = document.querySelector(`[data-mode="${mode}"]`);
    if (selectedBtn) {
      selectedBtn.classList.add("active");
    }
  }

  /**
   * Set the active question type by updating card styles
   */
  private setActiveQuestionType(type: string): void {
    const questionTypeCards = document.querySelectorAll(".question-type-card");
    questionTypeCards.forEach((card) => {
      const cardElement = card as HTMLElement;
      cardElement.classList.toggle("active", cardElement.dataset.type === type);
    });
  }

  /**
   * Check if smart mode is enabled
   */
  public isSmartModeEnabled(): boolean {
    try {
      return this.elements.smartModeToggle?.checked || false;
    } catch (error) {
      console.error("Error checking smart mode status:", error);
      return false;
    }
  }

  /**
   * Get current mode selection
   */
  public getCurrentMode(): string {
    return this.elements.modeSelect?.value || "all";
  }

  /**
   * Get current question type selection
   */
  public getCurrentQuestionType(): QuestionType["type"] {
    return (
      (this.elements.questionTypeSelect?.value as QuestionType["type"]) ||
      "fill-blank"
    );
  }

  /**
   * Handle reset confirmation
   */
  private async confirmReset(): Promise<void> {
    try {
      const resetStatsCheck = document.getElementById(
        "reset-stats-check"
      ) as HTMLInputElement;
      const resetStreaksCheck = document.getElementById(
        "reset-streaks-check"
      ) as HTMLInputElement;
      const resetXpCheck = document.getElementById(
        "reset-xp-check"
      ) as HTMLInputElement;
      const resetAchievementsCheck = document.getElementById(
        "reset-achievements-check"
      ) as HTMLInputElement;

      const options = {
        resetStats: resetStatsCheck?.checked || false,
        resetStreaks: resetStreaksCheck?.checked || false,
        resetXP: resetXpCheck?.checked || false,
        resetAchievements: resetAchievementsCheck?.checked || false,
      };

      // Check if at least one option is selected
      if (!Object.values(options).some((checked) => checked)) {
        await dialogManager.showWarning(
          "Please select at least one option to reset.",
          {
            title: "No Options Selected",
          }
        );
        return;
      }

      // Build confirmation message
      const selectedItems: string[] = [];
      if (options.resetStats) selectedItems.push("Learning Statistics");
      if (options.resetStreaks) selectedItems.push("Streaks");
      if (options.resetXP) selectedItems.push("XP & Level");
      if (options.resetAchievements) selectedItems.push("Achievements");

      const confirmMessage = `Are you sure you want to reset: ${selectedItems.join(
        ", "
      )}?\n\nThis cannot be undone.`;

      const confirmed = await dialogManager.showConfirm(confirmMessage, {
        title: "Reset Confirmation",
        icon: "⚠️",
      });

      if (confirmed) {
        // Pass confirmReset: false to avoid double confirmation
        eventManager.emit("ui:reset-stats", {
          ...options,
          confirmReset: false,
        });
      }
    } catch (error) {
      console.error("Error confirming reset:", error);
      this.showMessage("Error resetting data. Please try again.", "error");
    }
  }

  /**
   * Cleanup and destroy UI manager
   */
  public destroy(): void {
    try {
      // QuestionDisplay cleanup if needed
      this.feedbackSystem?.destroy?.();
      this.statsDashboard?.destroy?.();
    } catch (error) {
      console.error("Error during UI manager cleanup:", error);
    }
  }

  /**
   * Show achievement notification
   */
  public showAchievementNotification(achievements: string[]): void {
    try {
      const notification = this.elements.achievementNotification;
      if (!notification || !achievements || achievements.length === 0) return;

      const descriptionElement = notification.querySelector(
        "#achievement-description"
      );
      if (descriptionElement) {
        descriptionElement.textContent = achievements.join(", ");
      }

      notification.style.display = "block";

      // Auto-hide after 4 seconds
      setTimeout(() => {
        if (notification) {
          notification.style.display = "none";
        }
      }, 4000);
    } catch (error) {
      console.error("Error showing achievement notification:", error);
    }
  }

  /**
   * Update basic stats display (correct count, total, accuracy)
   */
  public updateBasicStats(stats: any): void {
    try {
      if (this.elements.correctCount) {
        this.elements.correctCount.textContent =
          stats.correct?.toString() || "0";
      }
      if (this.elements.totalCount) {
        this.elements.totalCount.textContent = stats.total?.toString() || "0";
      }
      if (this.elements.accuracyPercent) {
        const accuracy =
          stats.total > 0 ? Math.round((stats.correct / stats.total) * 100) : 0;
        this.elements.accuracyPercent.textContent = `${accuracy}%`;
      }

      // If detailed stats are currently visible, refresh their content
      this.refreshDetailedStatsIfVisible();
    } catch (error) {
      console.error("Error updating basic stats:", error);
    }
  }

  /**
   * Show progressive info tooltip
   */
  private showProgressiveInfoTooltip(): void {
    // Create tooltip if it doesn't exist
    let tooltip = document.getElementById("progressive-info-tooltip");
    if (!tooltip) {
      tooltip = document.createElement("div");
      tooltip.id = "progressive-info-tooltip";
      tooltip.className = "progressive-info-tooltip";
      tooltip.innerHTML = `
        <h5>🚀 How Smart Learning Works</h5>
        <div class="info-step">
          <span class="step-icon">🎯</span>
          <span><strong>Multiple Choice:</strong> Build recognition & confidence</span>
        </div>
        <div class="info-step">
          <span class="step-icon">✏️</span>
          <span><strong>Fill the Blank:</strong> Practice recall & spelling</span>
        </div>
        <div class="info-step">
          <span class="step-icon">🌍</span>
          <span><strong>Translation:</strong> Master real-world usage</span>
        </div>
        <p style="margin-top: 10px; font-size: 12px; color: #6c757d;">
          Questions automatically progress based on your performance!
        </p>
      `;
      document.body.appendChild(tooltip);
    }

    // Toggle tooltip visibility
    const infoBtn = document.getElementById("progressive-info-btn");
    const isVisible = tooltip.classList.contains("show");

    if (isVisible) {
      // Hide tooltip
      tooltip.classList.remove("show");
    } else {
      // Show tooltip
      if (infoBtn && tooltip) {
        const rect = infoBtn.getBoundingClientRect();
        tooltip.style.position = "fixed";
        tooltip.style.top = `${rect.bottom + 8}px`;
        tooltip.style.left = `${Math.max(10, rect.left - 100)}px`;
        tooltip.classList.add("show");

        // Hide tooltip when clicking elsewhere (but not on the info button)
        const hideTooltip = (e: Event) => {
          if (!tooltip?.contains(e.target as Node) && e.target !== infoBtn) {
            tooltip?.classList.remove("show");
            document.removeEventListener("click", hideTooltip);
          }
        };

        // Add listener after a small delay to prevent immediate hiding
        setTimeout(() => {
          document.addEventListener("click", hideTooltip);
        }, 100);
      }
    }
  }

  /**
   * Auto-scroll to keep the feedback area in view
   */
  private scrollToFeedback(): void {
    const feedbackElement = this.elements.feedback;
    if (feedbackElement && feedbackElement.style.display !== "none") {
      // Scroll to feedback with some padding above
      const yOffset = -100; // 100px padding above feedback
      const elementTop =
        feedbackElement.getBoundingClientRect().top +
        window.pageYOffset +
        yOffset;

      window.scrollTo({
        top: elementTop,
        behavior: "smooth",
      });
    }
  }

  /**
   * Auto-scroll to keep the question area in focus
   */
  private scrollToQuestion(): void {
    const questionText = this.elements.questionText;
    if (questionText) {
      const yOffset = -100; // 100px padding above question
      const elementTop =
        questionText.getBoundingClientRect().top + window.pageYOffset + yOffset;

      window.scrollTo({
        top: elementTop,
        behavior: "smooth",
      });
    }
  }

  /**
   * Initialize the dropdown menu for secondary actions
   */
  private initializeDropdownMenu(): void {
    const moreActionsBtn = document.getElementById("more-actions-btn");
    const actionsDropdown = document.getElementById("actions-dropdown");

    if (!moreActionsBtn || !actionsDropdown) {
      return;
    }

    // Toggle dropdown on button click
    moreActionsBtn.addEventListener("click", (e) => {
      e.stopPropagation();
      const isVisible = actionsDropdown.style.display === "block";
      actionsDropdown.style.display = isVisible ? "none" : "block";
    });

    // Close dropdown when clicking outside
    document.addEventListener("click", (e) => {
      if (
        !moreActionsBtn.contains(e.target as Node) &&
        !actionsDropdown.contains(e.target as Node)
      ) {
        actionsDropdown.style.display = "none";
      }
    });

    // Close dropdown when pressing Escape
    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape") {
        actionsDropdown.style.display = "none";
      }
    });

    // Close dropdown when selecting an item
    actionsDropdown.addEventListener("click", (e) => {
      if ((e.target as HTMLElement).classList.contains("dropdown-item")) {
        actionsDropdown.style.display = "none";
      }
    });
  }

  /**
   * Shows stats with smart scroll and visual feedback
   */
  private showStatsWithAttention(): void {
    const detailedStats = document.getElementById("detailed-stats");

    if (detailedStats) {
      // Show the stats
      detailedStats.style.display = "block";

      // Trigger StatsDashboard.show() for content
      try {
        if (this.statsDashboard && this.statsDashboard.show) {
          this.statsDashboard.show();
        }
      } catch {
        // Fallback: emit the event if direct call fails
      }

      // Also emit the event as backup
      eventManager.emit("ui:show-detailed-stats", undefined);

      // Smart scroll with a slight delay to ensure content is rendered
      setTimeout(() => {
        detailedStats.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });

        // Add visual attention effect
        detailedStats.classList.add("stats-attention");

        // Remove the attention effect after animation
        setTimeout(() => {
          detailedStats.classList.remove("stats-attention");
        }, 2000);
      }, 100);
    }
  }

  /**
   * Initialize header action buttons
   */
  private initializeHeaderActions(): void {
    // Header Stats Button
    const headerStatsBtn = document.getElementById("header-stats-btn");
    if (headerStatsBtn) {
      headerStatsBtn.addEventListener("click", () => {
        // Use smart scroll with visual feedback
        this.showStatsWithAttention();
      });
    }

    // Header Notifications Button
    const headerNotificationsBtn = document.getElementById(
      "header-notifications-btn"
    );
    if (headerNotificationsBtn) {
      headerNotificationsBtn.addEventListener("click", async () => {
        // Use the same logic as the existing notifications button
        if ((window as any).initializeNotifications) {
          const success = await (window as any).initializeNotifications();
          const status = (window as any).getNotificationStatus?.() || {
            success,
            message: success
              ? "Notifications enabled!"
              : "Failed to enable notifications.",
            isDevelopment: false,
          };

          if (status.success) {
            await dialogManager.showSuccess(`🔔 ${status.message}`, {
              title: "Notifications Enabled",
            });
          } else {
            await dialogManager.showError(`❌ ${status.message}`, {
              title: "Notifications Failed",
            });
          }
        }
      });
    }

    // Header Help Button
    const headerHelpBtn = document.getElementById("header-help-btn");
    const helpModal = document.getElementById("help-modal");
    if (headerHelpBtn && helpModal) {
      headerHelpBtn.addEventListener("click", () => {
        helpModal.style.display = "block";
      });
    }

    // Header Settings Button
    const headerSettingsBtn = document.getElementById("header-settings-btn");
    const settingsModal = document.getElementById("settings-modal");
    if (headerSettingsBtn && settingsModal) {
      headerSettingsBtn.addEventListener("click", () => {
        settingsModal.style.display = "block";
      });
    }

    // Settings Modal Close Button
    const closeSettingsBtn = document.getElementById("close-settings-btn");
    if (closeSettingsBtn && settingsModal) {
      closeSettingsBtn.addEventListener("click", () => {
        settingsModal.style.display = "none";
      });

      // Close when clicking outside the modal
      settingsModal.addEventListener("click", (e) => {
        if (e.target === settingsModal) {
          settingsModal.style.display = "none";
        }
      });
    }
  }
}

// Make UIManager globally available for backward compatibility
(window as any).UIManager = UIManager;
